<script setup lang="ts">
import { computed, type HTMLAttributes, inject } from 'vue';
import { tabsInjectionKey } from './internal';

const props = defineProps<
  { tabId: string; tabLabel: string; tabCount?: number } & /* @vue-ignore */ HTMLAttributes
>();

const tabsInjection = inject(tabsInjectionKey);
const ariaSelected = computed(() =>
  tabsInjection?.selectedTabId.value === props.tabId ? 'true' : 'false'
);
const tabindex = computed(() => (tabsInjection?.selectedTabId.value === props.tabId ? '0' : '-1'));

const setSelected = () => {
  if (!tabsInjection || tabsInjection.selectedTabId.value === props.tabId) return;

  tabsInjection.setSelectedTab(props.tabId);
};

const moveFocusToTab = (tabId: string) => {
  if (!tabsInjection || tabId === props.tabId) return;

  // Attempt to focus the given tab unless it's the current one already
  document.getElementById(tabsInjection.getTabElementId(tabId))?.focus();
};

const moveFocusToPreviousTab = () => {
  if (!tabsInjection) return;

  const availableTabs = [...tabsInjection.tabs.value.keys()];
  const currentIndex = availableTabs.indexOf(props.tabId);
  moveFocusToTab(availableTabs[(currentIndex - 1 + availableTabs.length) % availableTabs.length]);
};
const moveFocusToNextTab = () => {
  if (!tabsInjection) return;

  const availableTabs = [...tabsInjection.tabs.value.keys()];
  const currentIndex = availableTabs.indexOf(props.tabId);
  moveFocusToTab(availableTabs[(currentIndex + 1) % availableTabs.length]);
};
const moveFocusToFirstTab = () => {
  if (!tabsInjection) return;

  const availableTabs = [...tabsInjection.tabs.value.keys()];
  moveFocusToTab(availableTabs[0]);
};
const moveFocusToLastTab = () => {
  if (!tabsInjection) return;

  const availableTabs = [...tabsInjection.tabs.value.keys()];
  moveFocusToTab(availableTabs[availableTabs.length - 1]);
};
</script>
<template>
  <button
    :id="tabsInjection?.getTabElementId($props.tabId)"
    role="tab"
    type="button"
    :aria-selected="ariaSelected"
    :aria-controls="tabsInjection?.getTabpanelElementId($props.tabId)"
    :tabindex="tabindex"
    @click="setSelected"
    @keydown.exact.enter.stop.prevent="setSelected"
    @keydown.exact.space.stop.prevent="setSelected"
    @keydown.exact.left.stop.prevent="moveFocusToPreviousTab"
    @keydown.exact.right.stop.prevent="moveFocusToNextTab"
    @keydown.exact.home.stop.prevent="moveFocusToFirstTab"
    @keydown.exact.end.stop.prevent="moveFocusToLastTab"
  >
    {{ $props.tabLabel }}
    <span v-if="$props.tabCount !== undefined" class="nx-tab__count">{{ $props.tabCount }}</span>
  </button>
</template>
<style scoped>
/* The indicator is drawn, not bordered: no layout shift between states, and it
   sits on the tablist's baseline like NxRouteTabs. */
[role='tab'] {
  position: relative;
  border: none;
  border-radius: var(--nxt-radius-sm) var(--nxt-radius-sm) 0 0;
  background: none;
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  color: var(--nxt-tab-fg);
  font-size: var(--nxt-tab-size);
  font-weight: var(--nxt-font-weight-medium);
  cursor: pointer;
  transition:
    color var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);

  &::after {
    content: '';
    position: absolute;
    inset-inline: var(--nxt-space-xs);
    inset-block-end: 0;
    height: calc(var(--nxt-hairline) * 2);
    border-radius: var(--nxt-radius-full) var(--nxt-radius-full) 0 0;
    background: transparent;
    transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
  }

  &:hover {
    color: var(--nxt-tab-fg-hover);
    background: color-mix(in srgb, var(--nxt-tab-bg-hover) 70%, transparent);
  }

  &:hover::after {
    background: var(--nxt-border-strong);
  }

  &[aria-selected='true'] {
    color: var(--nxt-fg);
  }

  &[aria-selected='true']::after {
    background: var(--nxt-tab-indicator);
  }

  &:focus-visible {
    outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
    outline-offset: calc(var(--nxt-hairline) * -2);
  }
}

.nx-tab__count {
  display: inline-block;
  min-width: calc(var(--nxt-size-unit) * 1.125);
  margin-inline-start: var(--nxt-space-2xs);
  padding-inline: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-normal);
  text-align: center;
}

[aria-selected='true'] > .nx-tab__count {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}

/* Compact tablist (NxTabs `compact`): tighter tabs that keep one line. */
.is-compact > [role='tab'] {
  flex-shrink: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  font-size: var(--nxt-font-size-sm);
  white-space: nowrap;
}

.is-wrapped > [role='tab'] {
  flex: 1 0 auto;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
}

@media (prefers-reduced-motion: reduce) {
  [role='tab'],
  [role='tab']::after {
    transition: none;
  }
}
</style>
